import { useEffect, useMemo, useState } from "react";
import { Lock } from "lucide-react";
import { num } from "../lib/format";
import { Segmented } from "./common";

const ROWS = [
  ["pf", "Equity requirement profile – proforma (memo)"],
  ["dep", "Equity deployed (−)"],
  ["tod", "Equity to deploy (−)"],
  ["ret", "EPIL equity return (+)"],
  ["prof", "EPIL profit (+)"],
];

const parse = (s) => {
  const t = String(s).replace(/[£,\s]/g, "").replace("−", "-");
  if (t === "" || t === "-") return 0;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

function Cell({ value, field, period, canEdit, onSave }) {
  const [v, setV] = useState(value ? String(value) : "");
  const [focus, setFocus] = useState(false);
  useEffect(() => { if (!focus) setV(value ? String(value) : ""); }, [value, focus]);
  const tid = `cell-${field}-${period}`;
  if (!canEdit) return <div className="px-2 py-1.5 text-right" data-testid={tid}>{num(value)}</div>;
  const commit = () => {
    setFocus(false);
    const n = parse(v);
    if (n === null) { setV(value ? String(value) : ""); return; }
    const final = field === "dep" || field === "tod" ? -Math.abs(n) : n;
    if (final !== (value || 0)) onSave(period, field, final);
  };
  return (
    <input className="cell-input font-figure text-xs" data-testid={tid} value={focus ? v : num(value) === "–" ? "" : num(value)}
      placeholder="–" onFocus={() => { setFocus(true); setV(value ? String(value) : ""); }}
      onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setV(value ? String(value) : ""); e.currentTarget.blur(); } }} />
  );
}

function visibleRange(grid, mode, win) {
  const w0 = grid.findIndex((g) => g.period === win.start);
  const w1 = w0 + win.quarters - 1;
  if (mode === "All") return [0, grid.length - 1];
  if (mode === "Window") return [w0, w1];
  const nz = grid.map((g, i) => (ROWS.some(([f]) => g[f]) ? i : -1)).filter((i) => i >= 0);
  if (!nz.length) return [w0, w1];
  return [Math.min(nz[0], w0), Math.max(nz[nz.length - 1], Math.min(w0 + 7, grid.length - 1))];
}

export function QuarterGrid({ grid, canEdit, window: win, onSave }) {
  const [mode, setMode] = useState("Active");
  const [a, b] = useMemo(() => visibleRange(grid, mode, win), [grid, mode, win]);
  const cols = grid.slice(a, b + 1);
  const total = (f) => grid.reduce((s, g) => s + (g[f] || 0), 0);
  const sticky = "sticky left-0 z-10 bg-card";
  return (
    <div data-testid="quarterly-grid">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <Segmented options={["Active", "Window", "All"]} value={mode} onChange={setMode} testid="grid-range" />
        {!canEdit && (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="grid-readonly-note">
            <Lock className="h-3.5 w-3.5" /> Read-only – your role can view but not edit cashflows
          </span>
        )}
        {canEdit && <span className="text-xs text-muted-foreground">Click a cell to edit · equity deployed is saved as negative · Enter to save</span>}
      </div>
      <div className="overflow-x-auto rounded-lg border grid-scroll">
        <table className="dt-lite text-xs">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className={`${sticky} bg-muted min-w-[210px] px-3 py-2 text-left font-semibold`}>£</th>
              <th className="px-3 py-2 text-right font-semibold min-w-[110px]">Total</th>
              {cols.map((g) => (
                <th key={g.period} className={`px-2 py-2 text-right font-semibold min-w-[96px] whitespace-nowrap ${g.period === win.start ? "border-l-2 border-primary" : ""}`}>{g.quarter}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map(([f, label]) => (
              <tr key={f} className={`border-b ${f === "pf" ? "text-muted-foreground italic" : ""}`}>
                <td className={`${sticky} px-3 py-1.5 font-medium whitespace-nowrap`}>{label}</td>
                <td className="px-3 py-1.5 text-right font-figure font-semibold" data-testid={`grid-total-${f}`}>{num(total(f))}</td>
                {cols.map((g) => (
                  <td key={g.period} className="p-0.5 font-figure">
                    <Cell value={g[f]} field={f} period={g.period} canEdit={canEdit} onSave={onSave} />
                  </td>
                ))}
              </tr>
            ))}
            <tr className="bg-muted/60 font-bold">
              <td className={`${sticky} bg-muted px-3 py-2`}>Net cash flow</td>
              <td className="px-3 py-2 text-right font-figure" data-testid="grid-total-net">{num(total("net"))}</td>
              {cols.map((g) => <td key={g.period} className={`px-2 py-2 text-right font-figure ${g.net < 0 ? "text-primary" : ""}`}>{num(g.net)}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
